import { createBrowserHistory } from "history";
import { combineReducers } from "redux";
import { apiMiddleware } from "redux-api-middleware";
import { createReduxHistoryContext, push } from "redux-first-history";
import {
    FLUSH,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
    REHYDRATE,
    persistReducer,
    persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import { configureStore } from "@reduxjs/toolkit";
import createSagaMiddleware from "redux-saga";
import { attachReduxStore } from "../components/utilities/FeatureFlags";
import {
    cacheMiddleware,
    rsaaExtenderMiddleware,
} from "../redux/middlewares/rsaaExtender";
import { clearClientSession } from "./actions/authentication";
import { rsaaErrorSerializerMiddleware } from "./middlewares/rsaaErrorSerializer";
import { realtimeRequestMiddleware } from "./middlewares/realtimeRequest";
import { licenseInfoRefreshMiddleware } from "./middlewares/licenseInfoRefresh";
import {
    attachWebsocketListener,
    websocketMiddleware,
} from "../redux/middlewares/websocket";
import { gaMiddleware } from "./middlewares/analytics";
import { userScopedPersistentActionMiddleware } from "./middlewares/userScopedPersistentAction";
import { createMigrations } from "./migrations";
import rootReducer from "./reducers";
import rootSaga from "./sagas";
import { imageApi } from "./services/imageApi";
import { pocketbookSearchApi } from "./services/pocketbookSearchApi";
import { typeaheadApi } from "./services/typeaheadApi";
import features from "./features";
import { withSessionBoundary } from "./sessionBoundaryReducer";
import { SESSION_CLEAR_BROADCAST_KEY } from "./sessionSecurity";

const browserHistory = createBrowserHistory();
const sagaMiddleware = createSagaMiddleware();

const persistConfig = {
    key: "root",
    version: 1,
    storage,
    whitelist: ["persistent"],
    migrate: createMigrations,
};

const { createReduxHistory, routerMiddleware, routerReducer } =
    createReduxHistoryContext({
        history: browserHistory,
        // other options if needed
    });

const combinedReducer = combineReducers({
    router: routerReducer,
    ...rootReducer,
    ...features,
    [imageApi.reducerPath]: imageApi.reducer,
    [pocketbookSearchApi.reducerPath]: pocketbookSearchApi.reducer,
    [typeaheadApi.reducerPath]: typeaheadApi.reducer,
});

export const store = configureStore({
    reducer: persistReducer(
        persistConfig,
        withSessionBoundary(combinedReducer)
    ),
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [
                    FLUSH,
                    REHYDRATE,
                    PAUSE,
                    PERSIST,
                    PURGE,
                    REGISTER,
                    // Ignore all RSAA error actions as they contain non-serializable Error objects
                    // This is a pattern match for all RSAA error action types
                ],
                ignoredActionsPaths: ["payload.error", "error", "meta.error"],
            },
        })
            .prepend(
                routerMiddleware,
                gaMiddleware,
                rsaaExtenderMiddleware,
                apiMiddleware,
                rsaaErrorSerializerMiddleware, // Must come after apiMiddleware
                cacheMiddleware,
                realtimeRequestMiddleware,
                licenseInfoRefreshMiddleware,
                websocketMiddleware,
                userScopedPersistentActionMiddleware,
                sagaMiddleware
            )
            .concat(
                pocketbookSearchApi.middleware,
                imageApi.middleware,
                typeaheadApi.middleware
            ),
});

attachWebsocketListener(store);
attachReduxStore(store);

if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
        if (event.key === SESSION_CLEAR_BROADCAST_KEY) {
            store.dispatch(clearClientSession());
        }
    });
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppStore = typeof store;

sagaMiddleware.run(rootSaga);

export const history = createReduxHistory(store);

export const historyPush = push;

export const persistor = persistStore(store);
